import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatCurrency, formatDate } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Users, BarChart3, FileText, DollarSign, Shield
} from 'lucide-react';

export default function SuperAdminPage() {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountUsers, setAccountUsers] = useState([]);
  const [subscriptionOverview, setSubscriptionOverview] = useState(null);
  const [subSearch, setSubSearch] = useState('');
  const [subPlanFilter, setSubPlanFilter] = useState('all');
  const [subStatusFilter, setSubStatusFilter] = useState('all');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [saveSubLoading, setSaveSubLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (err) {
      toast.error('Failed to load system stats');
      console.error(err);
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/accounts');
      setAccounts(response.data);
    } catch (err) {
      toast.error('Failed to load accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to load users');
      console.error(err);
    }
  }, []);

  const fetchAccountUsers = async (accountId) => {
    try {
      const response = await api.get(`/admin/accounts/${accountId}/users`);
      setAccountUsers(response.data);
      setSelectedAccount(accountId);
    } catch (err) {
      toast.error('Failed to load account users');
      console.error(err);
    }
  };

  const fetchSubscriptions = useCallback(async (opts = {}) => {
    try {
      const params = {};
      const searchValue = opts.search ?? subSearch;
      const planValue = opts.plan ?? subPlanFilter;
      const statusValue = opts.status ?? subStatusFilter;
      if (searchValue?.trim()) params.search = searchValue.trim();
      if (planValue && planValue !== 'all') params.plan = planValue;
      if (statusValue && statusValue !== 'all') params.status = statusValue;
      const response = await api.get('/admin/subscriptions/overview', { params });
      setSubscriptionOverview(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
      console.error(err);
    }
  }, [subSearch, subPlanFilter, subStatusFilter]);

  useEffect(() => {
    fetchStats();
    fetchAccounts();
    fetchAllUsers();
    fetchSubscriptions();
  }, [fetchStats, fetchAccounts, fetchAllUsers, fetchSubscriptions]);

  const openEditSubscription = (row) => {
    setEditingSub({
      account_id: row.account_id,
      company_name: row.company_name,
      plan_slug: row.plan_slug || 'free',
      status: row.status || 'active',
      stripe_customer_id: row.stripe_customer_id || '',
      stripe_subscription_id: row.stripe_subscription_id || '',
      current_period_start: row.current_period_start || '',
      current_period_end: row.current_period_end || '',
      source: row.subscription_source || row.billing_source || 'manual',
    });
    setEditModalOpen(true);
  };

  const saveSubscription = async () => {
    if (!editingSub) return;
    setSaveSubLoading(true);
    try {
      const payload = {
        plan_slug: editingSub.plan_slug,
        status: editingSub.status,
        stripe_customer_id: editingSub.stripe_customer_id || null,
        stripe_subscription_id: editingSub.stripe_subscription_id || null,
      };
      if (editingSub.current_period_start) payload.current_period_start = editingSub.current_period_start;
      if (editingSub.current_period_end) payload.current_period_end = editingSub.current_period_end;

      await api.put(`/admin/subscriptions/${editingSub.account_id}`, payload);
      toast.success('Subscription updated');
      setEditModalOpen(false);
      await fetchSubscriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaveSubLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Super Admin Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Platform-level system administration</p>
        </div>
        <Badge className="bg-purple-100 text-purple-800 text-sm px-3 py-1">
          <Shield className="w-4 h-4 mr-1" />
          Super Admin
        </Badge>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Total Accounts</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_accounts}</p>
                </div>
                <Building2 className="w-10 h-10 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_users}</p>
                </div>
                <Users className="w-10 h-10 text-emerald-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Total Invoices</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_invoices}</p>
                </div>
                <FileText className="w-10 h-10 text-amber-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Platform Revenue</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(stats.total_revenue)}</p>
                </div>
                <DollarSign className="w-10 h-10 text-rose-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="stats"><BarChart3 className="w-4 h-4 mr-2" />Statistics</TabsTrigger>
          <TabsTrigger value="accounts"><Building2 className="w-4 h-4 mr-2" />Accounts</TabsTrigger>
          <TabsTrigger value="subscriptions"><DollarSign className="w-4 h-4 mr-2" />Subscriber</TabsTrigger>
          <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="mt-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Platform Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {stats && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-slate-200 p-4 rounded-md">
                    <p className="text-sm text-slate-500">Projects</p>
                    <p className="text-xl font-bold text-slate-900">{stats.total_projects}</p>
                  </div>
                  <div className="border border-slate-200 p-4 rounded-md">
                    <p className="text-sm text-slate-500">Clients</p>
                    <p className="text-xl font-bold text-slate-900">{stats.total_clients}</p>
                  </div>
                  <div className="border border-slate-200 p-4 rounded-md">
                    <p className="text-sm text-slate-500">Quotes</p>
                    <p className="text-xl font-bold text-slate-900">{stats.total_quotes}</p>
                  </div>
                  <div className="border border-slate-200 p-4 rounded-md">
                    <p className="text-sm text-slate-500">Estimates</p>
                    <p className="text-xl font-bold text-slate-900">{stats.total_estimates}</p>
                  </div>
                  <div className="border border-slate-200 p-4 rounded-md">
                    <p className="text-sm text-slate-500">Active Subscriptions</p>
                    <p className="text-xl font-bold text-slate-900">{stats.active_subscriptions}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts" className="mt-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>All Accounts</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <p className="p-6 text-slate-500">Loading accounts...</p>
              ) : accounts.length === 0 ? (
                <p className="p-6 text-slate-500">No accounts found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Users</TableHead>
                      <TableHead className="text-right">Projects</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account.id} className="cursor-pointer hover:bg-slate-50" onClick={() => fetchAccountUsers(account.id)}>
                        <TableCell className="font-medium">{account.company_name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{account.company_email}</TableCell>
                        <TableCell className="text-right">{account.user_count}</TableCell>
                        <TableCell className="text-right">{account.project_count}</TableCell>
                        <TableCell className="text-right">{account.invoice_count}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(account.total_revenue)}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            account.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                            account.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {account.subscription_status || 'none'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(account.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {selectedAccount && accountUsers.length > 0 && (
            <Card className="border border-slate-200 shadow-sm mt-4">
              <CardHeader>
                <CardTitle>Account Users</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accountUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.first_name} {user.last_name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge className="text-xs bg-blue-100 text-blue-800">{user.role}</Badge>
                        </TableCell>
                        <TableCell>
                          {user.email_verified ? (
                            <Badge className="text-xs bg-emerald-100 text-emerald-800">Verified</Badge>
                          ) : (
                            <Badge className="text-xs bg-amber-100 text-amber-800">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(user.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-4 space-y-4">
          {subscriptionOverview && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Total</p><p className="text-lg font-semibold">{subscriptionOverview.total_signed_up_accounts}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Free</p><p className="text-lg font-semibold">{subscriptionOverview.free_accounts}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Paid</p><p className="text-lg font-semibold">{subscriptionOverview.paid_accounts}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Pro</p><p className="text-lg font-semibold">{subscriptionOverview.pro_accounts}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Business</p><p className="text-lg font-semibold">{subscriptionOverview.business_accounts}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Past Due</p><p className="text-lg font-semibold">{subscriptionOverview.past_due_count}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Cancelled</p><p className="text-lg font-semibold">{subscriptionOverview.cancelled_count}</p></CardContent></Card>
              <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Trialing</p><p className="text-lg font-semibold">{subscriptionOverview.trialing_count}</p></CardContent></Card>
            </div>
          )}

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Customer Subscription Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  placeholder="Search company or email"
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                />
                <Select value={subPlanFilter} onValueChange={setSubPlanFilter}>
                  <SelectTrigger><SelectValue placeholder="Plan filter" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All plans</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={subStatusFilter} onValueChange={setSubStatusFilter}>
                  <SelectTrigger><SelectValue placeholder="Status filter" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trialing">Trialing</SelectItem>
                    <SelectItem value="past_due">Past due</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => fetchSubscriptions()}>Apply Filters</Button>
              </div>

              {!subscriptionOverview ? (
                <p className="text-slate-500">Loading subscriptions...</p>
              ) : subscriptionOverview.rows.length === 0 ? (
                <p className="text-slate-500">No subscriptions found for current filters.</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Primary Email</TableHead>
                        <TableHead>Signed Up</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Stripe Customer</TableHead>
                        <TableHead>Stripe Subscription</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptionOverview.rows.map((row) => (
                        <TableRow key={row.account_id}>
                          <TableCell className="font-medium">{row.company_name}</TableCell>
                          <TableCell className="text-sm">{row.primary_user_email || row.company_email || '-'}</TableCell>
                          <TableCell className="text-sm">{formatDate(row.created_at)}</TableCell>
                          <TableCell>{row.plan_display_name}</TableCell>
                          <TableCell><Badge>{row.status}</Badge></TableCell>
                          <TableCell>{row.subscription_source}</TableCell>
                          <TableCell className="text-xs">{row.stripe_customer_id || '-'}</TableCell>
                          <TableCell className="text-xs">{row.stripe_subscription_id || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => openEditSubscription(row)}>Edit</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>All Users (Last 100)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {users.length === 0 ? (
                <p className="p-6 text-slate-500">No users found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.first_name} {user.last_name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${
                            user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'account_owner' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.email_verified ? (
                            <Badge className="text-xs bg-emerald-100 text-emerald-800">Verified</Badge>
                          ) : (
                            <Badge className="text-xs bg-amber-100 text-amber-800">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(user.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Subscription - {editingSub?.company_name}</DialogTitle>
          </DialogHeader>
          {editingSub && (
            <div className="space-y-4">
              {editingSub.source === 'stripe' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  This account is Stripe-backed. Manual edits here are explicit overrides.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select value={editingSub.plan_slug} onValueChange={(value) => setEditingSub({ ...editingSub, plan_slug: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editingSub.status} onValueChange={(value) => setEditingSub({ ...editingSub, status: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trialing">Trialing</SelectItem>
                      <SelectItem value="past_due">Past Due</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stripe Customer ID</Label>
                  <Input value={editingSub.stripe_customer_id} onChange={(e) => setEditingSub({ ...editingSub, stripe_customer_id: e.target.value })} placeholder="cus_..." />
                </div>
                <div className="space-y-2">
                  <Label>Stripe Subscription ID</Label>
                  <Input value={editingSub.stripe_subscription_id} onChange={(e) => setEditingSub({ ...editingSub, stripe_subscription_id: e.target.value })} placeholder="sub_..." />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={saveSubscription} disabled={saveSubLoading}>
              {saveSubLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
